import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { BOARDS_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import type { BoardContextType } from '@/features/CustomFeedV2/common';

const QUERY_KEY = 'feed-definition';

export const useFetchFeed = (
  boardId: string,
  refetchInterval?: RefetchIntervalType,
  enabled = true,
  refetchIntervalInBackground = false
) => {
  return useQuery([QUERY_KEY, boardId], () => fetcher(boardId), {
    select: (data) => data.data.results,
    enabled: !!boardId && enabled,
    ...(refetchInterval && { refetchInterval }),
    refetchIntervalInBackground: refetchIntervalInBackground,
  });
};

export const useInvalidateFetchFeed = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (boardId: string) => {
      return queryClient.invalidateQueries([QUERY_KEY, boardId]);
    },
    [queryClient]
  );
};

export const usePrefetchFeed = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (boardId: string) => {
      queryClient.prefetchQuery([QUERY_KEY, boardId], () => fetcher(boardId));
    },
    [queryClient]
  );
};

export const useInvalidateAllFeedDetails = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    return queryClient.invalidateQueries([QUERY_KEY], {
      refetchInactive: true,
    });
  }, [queryClient]);
};

const fetcher = (boardId: string) => {
  return axiosInstance.get<{ results: BoardContextType }>(
    BOARDS_API_ROUTES.boardInfoV2(boardId)
  );
};

type RefetchIntervalType = number | false | (() => number);
